import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ANY_COACH } from '@salaries/shared';
import { deleteHugimPercent, getHugimSettings, upsertHugimPercent } from '../../api/hugim';
import { byCourse, CoursePercentList, type CourseRolePercent } from './CoursePercentList';

// Per-hug percentage overrides for a hugim box, one per role (primary /
// secondary staff member). The global defaults live in the parent settings
// form; per-staff overrides live in the personal "הגדרות שכר פרטניות" tab.
// Precedence, within a role: staff+hug > staff > hug > global. Saves immediately.
export function HugimOverridesSection() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['hugim-percentages'], queryFn: getHugimSettings });

  const hugOverrides = byCourse(
    (settingsQ.data?.overrides ?? []).filter(
      (o) => o.coach_arbox_users_boxes_id === ANY_COACH && o.hug_key !== '',
    ),
  );

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['hugim-percentages'] });
    void qc.invalidateQueries({ queryKey: ['payroll-report'] });
  };
  const save = useMutation({
    mutationFn: async (e: CourseRolePercent) => {
      const base = { coach_arbox_users_boxes_id: ANY_COACH, hug_key: e.hug_key };
      if (e.primary != null) await upsertHugimPercent({ ...base, percent: e.primary, role: 'primary' });
      if (e.secondary != null) await upsertHugimPercent({ ...base, percent: e.secondary, role: 'secondary' });
    },
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message || 'שגיאה בשמירה'),
  });
  const remove = useMutation({
    mutationFn: async (e: CourseRolePercent) => {
      if (e.primary != null) await deleteHugimPercent(ANY_COACH, e.hug_key, 'primary');
      if (e.secondary != null) await deleteHugimPercent(ANY_COACH, e.hug_key, 'secondary');
    },
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message || 'שגיאה במחיקה'),
  });

  return (
    <CoursePercentList
      rows={hugOverrides}
      adding={save.isPending}
      onAdd={(hug_key, primary, secondary, done) =>
        save.mutate({ hug_key, primary, secondary }, { onSuccess: done })
      }
      onRemove={(hug) => {
        const e = hugOverrides.find((o) => o.hug_key === hug);
        if (e) remove.mutate(e);
      }}
    />
  );
}
